import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Alert, buttonClass } from './components';
import { CloseIcon, UploadIcon } from './icons';

/** The texts of a drop card; they come from the caller's dictionary. */
export interface FileDropWords {
  /** The invitation to drag ("drag an image here"). */
  drop: string;
  /** The word between dragging and choosing ("or"). */
  or: string;
  /** The button that opens the file chooser. */
  choose: string;
  /** Shown while the caller works on the files. */
  busy: string;
  /** The accessible name of the close button. */
  close: string;
}

function hasFiles(event: DragEvent): boolean {
  return event.dataTransfer.types.includes('Files');
}

/**
 * A card that takes files by dragging them onto it or by choosing them. It only hands the files
 * to the caller: checking, uploading and closing are the caller's.
 */
export function FileDropCard({
  title,
  words,
  accept,
  multiple = false,
  busy = false,
  hint,
  error,
  onFiles,
  onClose,
  testId,
}: {
  title: string;
  words: FileDropWords;
  /** The `accept` of the file chooser. Dropped files are not filtered: the caller checks them. */
  accept?: string;
  multiple?: boolean;
  busy?: boolean;
  hint?: ReactNode;
  error?: string | undefined;
  onFiles: (files: File[]) => void;
  onClose: () => void;
  /** The test hook of the file input. */
  testId?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    input.current?.focus();
  }, []);

  const take = (files: FileList | null) => {
    if (busy || files === null || files.length === 0) return;
    onFiles(multiple ? [...files] : [...files].slice(0, 1));
  };

  return (
    <div
      role="group"
      aria-label={title}
      aria-busy={busy}
      data-dragging={dragging}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !busy) onClose();
      }}
      onDragOver={(event) => {
        if (!hasFiles(event)) return;
        // Without this the browser opens the file instead of dropping it here.
        event.preventDefault();
        setDragging(!busy);
      }}
      onDragLeave={(event) => {
        // Moving over a child of the card is not leaving it.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={(event) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDragging(false);
        take(event.dataTransfer.files);
      }}
      className={`relative flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors ${dragging ? 'border-brand-600 bg-brand-50' : 'border-slate-300 bg-slate-50'}`}
    >
      <button
        type="button"
        aria-label={words.close}
        disabled={busy}
        onClick={onClose}
        className="absolute end-2 top-2 rounded-md p-1 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:cursor-not-allowed disabled:text-slate-300"
      >
        <CloseIcon />
      </button>
      <UploadIcon className="size-8 text-slate-400" />
      <p className="text-sm font-medium text-slate-800">{title}</p>
      {busy ? (
        <p role="status" className="text-sm text-slate-600">
          {words.busy}
        </p>
      ) : (
        <>
          <p className="text-sm text-slate-600">{words.drop}</p>
          <p className="text-xs text-slate-500">{words.or}</p>
        </>
      )}
      <label
        className={`${buttonClass('secondary')} cursor-pointer focus-within:outline-2 focus-within:outline-brand-600 ${busy ? 'hidden' : ''}`}
      >
        {words.choose}
        <input
          ref={input}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={busy}
          data-testid={testId}
          onChange={(event) => {
            take(event.target.files);
            // The same file can be chosen again after a failed attempt.
            event.target.value = '';
          }}
          className="sr-only"
        />
      </label>
      {hint === undefined ? null : <div className="text-xs text-slate-500">{hint}</div>}
      {error === undefined ? null : <Alert>{error}</Alert>}
    </div>
  );
}
